'use strict'

// React OOTB Hooks
import {useContext} from 'react'
import {useHistory} from 'react-router-dom'

// PWA OOTB Hooks
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from './use-multi-site'

// PWA OOTB Config
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Utils
import {validateRecaptcha} from '../utils/recaptchaUtils'

// Custom API
import AlertMgr from '../custom-api/alert-custom-api'
import ContactUs from '../custom-api/contact-us-custom-api'

// Custom Hooks
import {useSplioService} from './../hooks/use-splio-service'
import {useXSSProtection} from './../hooks/use-xss-protection.js'
import useShipToCountry from './../hooks/use-ship-to-country'

// Custom Context
import {IPContext} from '../custom-contexts'

import {findDialCodeByCountry} from '@app/utils/utils'

/*
How to use?
(...)
import ReCAPTCHA from 'react-google-recaptcha';
const MyComponent = () => {
    const config = getConfig()
    const [siteKey, setSiteKey] = useState(config.app.RECAPTCHA_SITE_KEY);
    const [captchaValue, setCaptchaValue] = useState(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    console.log("Captcha value:", value);
  };

  return (
    <div>
      <form>
        <ReCAPTCHA
          sitekey={siteKey}
          onChange={handleCaptchaChange}
        />
        <button type="submit" disabled={!captchaValue}>
          Submit
        </button>
      </form>
    </div>
  );
}
*/

/**
 * Custom hook for handling the "Contact Us" form submission.
 *
 * This hook provides functionality to submit a contact form to the server.
 * It prepares the necessary payload, manages the server response, and handles
 * any potential errors during the submission process.
 *
 * @returns {Object} An object containing the `submitContactUs` function.
 *
 * @property {Function} submitContactUs - Submits the contact us form data to the server.
 *
 * @async
 * @param {Object} formPayload - The payload containing form data.
 * @param {string} [formPayload.firstName] - The first name of the user.
 * @param {string} [formPayload.lastName] - The last name of the user.
 * @param {string} [formPayload.email] - The email address of the user.
 * @param {string} [formPayload.phone] - The phone number of the user.
 * @param {string} [formPayload.orderNumber] - The order number related to the inquiry.
 * @param {string} [formPayload.country] - The country of the user.
 * @param {string} [formPayload.question] - The question category or type.
 * @param {string} [formPayload.message] - The message or inquiry details from the user.
 * @param {string} [formPayload.captchaCode] - The captcha code for verification.
 * @returns {Promise<Object>} A promise that resolves to an object containing:
 *   @property {boolean} success - Indicates if the submission was successful.
 *   @property {string} msg - A message detailing the result of the submission.
 */
export const useContactUs = () => {
    const {getTokenWhenReady} = useAccessToken()
    const {forwardAccountToSplio} = useSplioService()
    const {site, locale, buildUrl} = useMultiSite()
    const {app: appConfig} = getConfig()
    const {isIPBlocked} = useContext(IPContext)
    const userIP = useContext(IPContext).ip
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()
    const {shipToCountry} = useShipToCountry()

    let alertMgr = new AlertMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })

    const contactUs = new ContactUs({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })

    return {
        /**
         * Submits the contact us form data to the server and returns the result.
         *
         * This asynchronous function handles the submission of a contact form by
         * preparing the necessary payload and sending it to the server. It manages
         * the response and any potential errors, returning an object indicating
         * the success status and a message.
         *
         * @async
         * @param {Object} formPayload - The payload containing form data.
         * @param {string} [formPayload.firstName] - The first name of the user.
         * @param {string} [formPayload.lastName] - The last name of the user.
         * @param {string} [formPayload.email] - The email address of the user.
         * @param {string} [formPayload.phone] - The phone number of the user.
         * @param {string} [formPayload.orderNumber] - The order number related to the inquiry.
         * @param {string} [formPayload.country] - The country of the user.
         * @param {string} [formPayload.question] - The question category or type.
         * @param {string} [formPayload.message] - The message or inquiry details from the user.
         * @param {string} [formPayload.captchaCode] - The captcha code for verification.
         * @returns {Promise<Object>} A promise that resolves to an object containing:
         *   @property {boolean} success - Indicates if the submission was successful.
         *   @property {string} msg - A message detailing the result of the submission.
         */
        async submitContactUs(formPayload) {
            let result = {
                success: true,
                msg: ''
            }

            // build body request
            const bodyRequest = {
                firstName: formPayload?.firstName,
                lastName: formPayload?.lastName,
                email: formPayload?.email,
                phone: formPayload?.phone
                    ? `${findDialCodeByCountry(formPayload?.phoneCountry)} ${
                          formPayload?.phone
                      }`.trim()
                    : '',
                orderNumber: formPayload?.orderNumber,
                country: formPayload?.country || shipToCountry?.id,
                myQuestion: formPayload?.question,
                comment: formPayload?.message
            }

            // get token
            const accessToken = await getTokenWhenReady()

            try {
                // validate IP
                if (isIPBlocked) {
                    const path = buildUrl('/')
                    history.push(path)
                    return
                }

                // validate all XSS form fields
                const isXssDetected = validateXssForm(formPayload)
                if (isXssDetected) {
                    return
                }

                // validate repatcha
                const isCaptchaValid = await validateRecaptcha(formPayload?.captchaCode, userIP)
                if (!isCaptchaValid || isCaptchaValid.success === false) {
                    result.msg = 'The captcha validation is invalid.'
                    result.success = false
                    return result
                }

                // call SFSC ticket creation
                // alert is already being done in SFCC side
                const {success, detail} = await contactUs.submitContactUs(
                    bodyRequest,
                    accessToken,
                    site.id,
                    locale?.id
                )
                if (!success) {
                    result.msg = detail
                    result.success = false
                    return result
                }
                // Call Splio
                if (formPayload?.consent) {
                    bodyRequest.marketingConsent = formPayload?.consent
                }
                bodyRequest.newsletterSection = 'body'
                bodyRequest.requestType = 'contact-us'
                await forwardAccountToSplio(bodyRequest)
            } catch (error) {
                await alertMgr.sendAlert(
                    {
                        context: {
                            siteId: site.id,
                            message: error.message,
                            initialRequest: bodyRequest,
                            category: 'Contact Form',
                            order: '',
                            page: '',
                            job: '',
                            asset: '',
                            identifier: '',
                            fileName: '',
                            lineNumber: ''
                        },
                        alertType: 'business',
                        logging: 'no'
                    },
                    accessToken
                )
                result.msg = error.message
                result.success = false
            }
            return result
        }
    }
}
